import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { domainControlsService } from '../../services';
import ScreenLoader from '../../Components/ScreenLoader';
import '../../styles/AddOns.scss';

function BankUserDomainControls() {
    const { frameworkId, domainId } = useParams();
    const [controls, setControls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [domainName, setDomainName] = useState('');
    const [frameworkName, setFrameworkName] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchControls = async () => {
            try {
                setLoading(true);
                const params = {
                    framework_id: frameworkId,
                    domain_id: domainId
                };
                const response = await domainControlsService(params);
                const data = response?.statusCode?.response_data?.data?.controls ||
                    response?.statusCode?.response_data?.data ||
                    response?.statusCode?.response_data ||
                    [];
                setControls(Array.isArray(data) ? data : []);
                setDomainName(localStorage.getItem('selectedDomainName') || 'Domain');
                setFrameworkName(localStorage.getItem('selectedFrameworkName') || 'Framework');
            } catch (error) {
                console.error('Error fetching domain controls:', error);
            } finally {
                setLoading(false);
            }
        };
        if (frameworkId && domainId) fetchControls();
    }, [frameworkId, domainId]);

    const getStatusColor = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'active' || s === 'compliant' || s === 'complete') return '#10B981';
        if (s === 'inactive' || s === 'non-compliant') return '#EF4444';
        if (s === 'pending' || s === 'partial') return '#F97316';
        return '#94A3B8';
    };

    const getIndicatorColor = (percent) => {
        if (percent >= 80) return '#10B981';
        if (percent >= 50) return '#F97316';
        return '#EF4444';
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <ScreenLoader />
            </div>
        );
    }

    return (
        <div style={{ padding: '0px 24px 24px' }}>
            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-4">
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    style={{ color: '#94A3B8', borderColor: '#334155' }}
                >
                    ← Back
                </button>
                <div>
                    <h3 className="color_white mb-0 font_20 font-600">{domainName}</h3>
                    <small style={{ color: '#64748b' }}>{frameworkName}</small>
                </div>
            </div>

            {/* Controls Grid */}
            {controls.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted" style={{ fontSize: '16px' }}>No controls found for this domain.</p>
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    width: '100%'
                }}>
                    {controls.map((control, idx) => {
                        const name = control?.control_title;
                        const status = control?.status || control?.compliance_status || 'Active';
                        const totalConfigs = control?.total_configs || control?.total_questions || control?.total || 0;
                        const activeConfigs = control?.active_configs || control?.answered || control?.passing || 0;
                        const percent = control?.compliance_percentage || control?.percentage || (totalConfigs > 0 ? Math.round((activeConfigs / totalConfigs) * 100) : 0);
                        const statusColor = getStatusColor(status);
                        const indicatorColor = getIndicatorColor(percent);
                        const displayValue = totalConfigs > 0 ? `${activeConfigs}/${totalConfigs}` : `${percent}%`;
                        const subtitle = totalConfigs > 0 ? 'Active Configs' : `${percent}% Complete`;

                        return (
                            <div
                                key={control?.id || idx}
                                className="domain-control-card"
                                onClick={() => {
                                    const controlId = control?.organization_domain_control_id || control?.control_id || control?.id;
                                    localStorage.setItem('selectedControlName', name || '');
                                    navigate(`/bank-user/framework/${frameworkId}/domain/${domainId}/control/${controlId}/questions`);
                                }}
                            >
                                <div className="d-flex w-100 h-100">
                                    {/* Left Side: Icon Container */}
                                    <div
                                        className="dcc-icon"
                                        style={{ background: `${statusColor}25` }}
                                    >
                                        {status.toLowerCase() === 'active' ? (
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={statusColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                                            </svg>
                                        ) : (
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={statusColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"></path>
                                                <path d="M12 16v-9"></path>
                                                <path d="m9 10 3-3 3 3"></path>
                                            </svg>
                                        )}
                                    </div>

                                    {/* Center: Title & Status */}
                                    <div className="dcc-info">
                                        <div className="dcc-title">
                                            {name}
                                        </div>
                                        <div
                                            className="dcc-status"
                                            style={{ color: statusColor }}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                                        </div>
                                    </div>

                                    {/* Right Side: Score & Subtitle */}
                                    <div className="dcc-score-box">
                                        <div
                                            className="dcc-value"
                                            style={{ color: totalConfigs > 0 ? '#ef4444' : '#06b6d4' }}
                                        >
                                            {displayValue}
                                        </div>
                                        <div className="dcc-subtitle">
                                            {subtitle}
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Right Status Square */}
                                <div
                                    className="dcc-indicator"
                                    style={{ background: indicatorColor }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default BankUserDomainControls;
